import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Dialog, Message, Form, Input, Radio } from '@fx-ui/fine-design';
import { RECEIPT_TYPE_MAP, API, RECEIPT_CONTENT_TYPE, TEXT_TYPE_REGEXP } from '~/constants';
import {
    isEmpty,
    forEach,
    get,
    set,
    post,
    getApi,
    getErrorMsg,
    assign,
    findIndex,
    isFileEmpty,
    isArray,
    trim,
    map,
    first
} from '~/utils';
import Intl from '@fx-ui/intl';
import { FileItem, FileUpload } from '~/components';
import { IFile } from '~/interfaces';
import { corpUserStore } from '~/models';

interface IReceiptDialogProps {
    mergeReceipts?: string[];
    receiptId?: string;
    showReceiptDialog: boolean;
    onCancel: () => void;
    onConfirmBtnClick?: () => void;
    onSubmitSuccess?: () => void;
}

const ReceiptDialog: React.FunctionComponent<IReceiptDialogProps> = (props) => {
    const {
        showReceiptDialog,
        onCancel,
        mergeReceipts,
        receiptId,
        onConfirmBtnClick,
        onSubmitSuccess
    } = props;

    const [defaultValue, setDefaultValue] = useState<any>();
    const [receiptType, setReceiptType] = useState<string>('e_plain');
    const [receiptTypes, setReceiptTypes] = useState<any[]>([]);
    const [formStage, setFormStage] = useState<boolean>(true);
    const [originFile, setOriginFile] = useState<IFile[]>();
    const firstFile = useMemo(() => first(originFile), [originFile]);
    const [dataValue, setDataValue] = useState<any>({ type: 'e_plain' });

    const taxRegex = '(^\\w{15}$)|(^\\w{18}$)';
    const notEmptyText = Intl.get('item.required');
    const receiptRef = useRef<any>();
    const deliveryRef = useRef<any>();

    const dataFields = {
        e_special: [
            'type',
            'tax_cert',
            'company_name',
            'tax_number',
            'reg_address',
            'reg_phone',
            'bank',
            'account',
            'receiver_email'
        ],
        e_personal: ['type', 'tax_number', 'full_name', 'receiver_email'],
        e_plain: [
            'type',
            'company_name',
            'tax_number',
            'reg_address',
            'reg_phone',
            'bank',
            'account',
            'receiver_email'
        ]
    };

    const receiptMap = {
        company_name: 'receipt.companyName',
        tax_cert: 'receipt.tax.cert',
        tax_number: 'receipt.taxNo',
        reg_address: 'receipt.regAddress',
        reg_phone: 'receipt.regPhone',
        bank: 'receipt.bank',
        account: 'receipt.account',
        full_name: 'name.common'
    };

    const receiptRules = {
        not_empty_rule: { type: 'string', range: [1, null], message: notEmptyText, required: true },
        tax_number: {
            type: 'string',
            validate: (value: string) => {
                return new RegExp(taxRegex).test(value);
            },
            message: (value: string) => {
                if (isEmpty(value)) {
                    return notEmptyText;
                } else {
                    return Intl.get('receipt.taxNo.required');
                }
            },
            required: true
        },
        tax_cert: {
            type: 'array',
            validate: (file: any) => {
                let valid = true;
                if (isEmpty(file)) {
                    valid = false;
                } else if (isArray(file) && isFileEmpty(file[0])) {
                    valid = false;
                }
                return valid;
            },
            message: notEmptyText,
            required: true
        }
    };

    const getReceiptRules = (type: string) => {
        const receiptFormRules = {};
        if (type === 'e_plain') {
            set(receiptFormRules, 'company_name', receiptRules.not_empty_rule);
            set(receiptFormRules, 'tax_number', receiptRules.tax_number);
        } else if (type === 'e_personal') {
            set(receiptFormRules, 'full_name', receiptRules.not_empty_rule);
        } else {
            forEach(receiptMap, (_v, name: string) => {
                if (name !== 'full_name') {
                    set(
                        receiptFormRules,
                        name,
                        get(receiptRules, name, receiptRules.not_empty_rule)
                    );
                }
            });
        }
        return receiptFormRules;
    };

    const deliveryRules = {
        receiver_email: {
            type: 'string',
            validate: (value: string) => {
                return new RegExp(TEXT_TYPE_REGEXP.EMAIL).test(value);
            },
            message: (value: string) => {
                if (isEmpty(value)) {
                    return notEmptyText;
                } else {
                    return Intl.get('email.invalid.warning');
                }
            },
            required: true
        } as const
    };

    useEffect(() => {
        if (showReceiptDialog) {
            setReceiptTypes(getReceiptTypes());
            getDefaultValue();
        } else {
            setReceiptTypes([]);
        }
    }, [showReceiptDialog]);

    useEffect(() => {
        if (!showReceiptDialog) {
            return;
        }
        receiptRef.current.core.updateOption({
            rules: getReceiptRules(receiptType)
        });
        deliveryRef.current.core.updateOption({
            rules: deliveryRules
        });
    }, [receiptType]);

    useEffect(() => {
        const formValue = receiptRef.current.core.getValue();
        receiptRef.current.core.updateOption({
            value: {
                ...formValue,
                tax_cert: originFile
            }
        });
    }, [originFile]);

    useEffect(() => {
        const receipValue = receiptRef.current.core.getValue();
        const deliveryValue = deliveryRef.current.core.getValue();
        if (!isEmpty(defaultValue)) {
            receiptRef.current.core.updateOption({
                value: assign(receipValue, defaultValue)
            });
            deliveryRef.current.core.updateOption({
                value: assign(deliveryValue, defaultValue)
            });
            const useDefault: boolean =
                findIndex(receiptTypes, (receiptType: any) => {
                    return receiptType.value === defaultValue.type;
                }) > -1;
            if (useDefault) {
                setReceiptType(defaultValue.type);
                setDataValue(defaultValue);
                setOriginFile(get(defaultValue, 'tax_cert'));
            }
        }
    }, [defaultValue]);

    const getDefaultValue = () => {
        post(getApi(API.order.get_default_receipt, corpUserStore.tenantId)).then((res: any) => {
            const receipt = get(res.data, 'receipt', {});
            setDefaultValue(receipt);
        });
    };

    const getReceiptTypes = () => {
        return [
            {
                value: 'e_plain',
                text: Intl.get(RECEIPT_TYPE_MAP.e_plain)
            },
            {
                value: 'e_special',
                text: Intl.get(RECEIPT_TYPE_MAP.e_special)
            },
            {
                value: 'e_personal',
                text: Intl.get(RECEIPT_TYPE_MAP.e_personal)
            }
        ];
    };

    const getAllowBlank = (receipItem: string) => {
        let allowBlank = true;
        switch (receiptType) {
            case 'e_plain':
            case 'e_special':
                if (receipItem === 'company_name' || receipItem === 'tax_number') {
                    allowBlank = false;
                } else {
                    allowBlank = true;
                }
                break;
            case 'e_personal':
                if (receipItem === 'full_name') {
                    allowBlank = false;
                } else {
                    allowBlank = true;
                }
                break;
            default:
                break;
        }
        return allowBlank;
    };

    // 开票信息
    const renderReceipInfo = () => {
        return (
            <div className="receipt-info-form">
                <div className="form-item receipt-info-title">{Intl.get('corp.receipt.info')}</div>
                <div className="form-item receipt-type">
                    <div className="item-label">{Intl.get('receipt.type')}</div>
                    {formStage ? (
                        <Radio.Group
                            style={{ width: 420 }}
                            value={receiptType}
                            onChange={(value: any) => {
                                setReceiptType(value);
                                changeDataValue('type', value);
                            }}
                        >
                            {map(receiptTypes, ({ value, text }) => {
                                return (
                                    <Radio value={value} key={value}>
                                        {text}
                                    </Radio>
                                );
                            })}
                        </Radio.Group>
                    ) : (
                        <div className="item-info">
                            {Intl.get(get(RECEIPT_TYPE_MAP, getValue('type')))}
                        </div>
                    )}
                </div>
                {formStage && (
                    <div className="receipt-electric-tip">{Intl.get('receipt.electric.tip')}</div>
                )}
                {renderReceipForm()}
                <div className="form-item receipt-content">
                    <div className="item-label">{Intl.get('receipt.content')}</div>
                    <span>{Intl.get(RECEIPT_CONTENT_TYPE.tech_service.text)}</span>
                    {formStage && (
                        <span className="tax-rate">
                            {Intl.get(RECEIPT_CONTENT_TYPE.tech_service.taxRate, {
                                taxRate: RECEIPT_CONTENT_TYPE.tech_service.tax
                            })}
                        </span>
                    )}
                </div>
                <div className="delivery-info-title">{Intl.get('receipt.sendMsg')}</div>
                {renderDeliveryForm()}
            </div>
        );
    };

    const renderReceipForm = () => {
        const receipItems =
            receiptType === 'e_personal'
                ? ['tax_number', 'full_name']
                : ['company_name', 'tax_number', 'reg_address', 'reg_phone', 'bank', 'account'];
        return (
            <Form ref={receiptRef} rules={getReceiptRules(receiptType)} className="receipt-form">
                {receiptType === 'e_special' && (
                    <Form.Item
                        label={Intl.get('receipt.tax.cert')}
                        className="receipt-tax-cert"
                        name="tax_cert"
                        required
                        labelWidth="120px"
                        showRequiredTip
                    >
                        {formStage ? (
                            <div className="tax-cert-content">
                                <FileUpload
                                    uploadTokenUrl={getApi(API.file.get_proof_token)}
                                    className="tax-cert-upload"
                                    style={{ width: 420 }}
                                    value={originFile}
                                    onChange={(files) => {
                                        setOriginFile(files);
                                        changeDataValue('tax_cert', files);
                                    }}
                                    maxSizeTip={Intl.get('upload.maxSizeTip', { size: 5 })}
                                />
                            </div>
                        ) : (
                            <div className="item-info">
                                {firstFile && <FileItem file={firstFile} style={{ width: 420 }} />}
                            </div>
                        )}
                    </Form.Item>
                )}
                {receipItems.map((receipItem: string) => {
                    return (
                        <Form.Item
                            key={receipItem}
                            label={Intl.get(get(receiptMap, receipItem))}
                            name={receipItem}
                            required={!getAllowBlank(receipItem)}
                            labelWidth="120px"
                            showRequiredTip
                            onChange={(value: string) => {
                                changeDataValue(receipItem, trim(value));
                            }}
                        >
                            {formStage ? (
                                <Input
                                    style={{ width: 420 }}
                                    placeholder={
                                        receipItem === 'tax_number'
                                            ? Intl.get('receipt.taxNo.required')
                                            : ''
                                    }
                                />
                            ) : (
                                <div className="item-info">{getValue(receipItem)}</div>
                            )}
                        </Form.Item>
                    );
                })}
            </Form>
        );
    };

    const renderDeliveryForm = () => {
        const deliveryItem = 'receiver_email';
        return (
            <Form ref={deliveryRef} rules={deliveryRules} className="delivery-form">
                <Form.Item
                    label={Intl.get('email')}
                    name={deliveryItem}
                    required
                    labelWidth="120px"
                    showRequiredTip
                    onChange={(value: string) => {
                        changeDataValue(deliveryItem, trim(value));
                    }}
                >
                    {formStage ? (
                        <Input style={{ width: 420 }} />
                    ) : (
                        <div className="item-info">{getValue(deliveryItem)}</div>
                    )}
                </Form.Item>
            </Form>
        );
    };

    const changeDataValue = (name: string, value: any) => {
        const newDataValue = { ...dataValue };
        set(newDataValue, name, value);
        setDataValue(newDataValue);
    };

    const handleOk = () => {
        getSubmitValue();
        if (formStage) {
            validateForm();
        } else {
            const orderIds = !isEmpty(receiptId) ? [receiptId] : mergeReceipts;
            const data = {
                orderIds: orderIds,
                receipt: getSubmitValue()
            };
            onConfirmBtnClick && onConfirmBtnClick();
            return post(getApi(API.order.issue_receipt, corpUserStore.tenantId), data, {
                customHandleHttpCodes: [400]
            })
                .then(() => {
                    onSubmitSuccess && onSubmitSuccess();
                    const submitDialog = Dialog.add({
                        className: 'fx-submit-success-dialog',
                        title: Intl.get('order.receipt.apply'),
                        separator: { footer: false },
                        content: (
                            <>
                                <div className="submit-success">{Intl.get('submit.success')}</div>
                                <div className="submit-success-tip">
                                    {Intl.get('receipt.submitSuccess.tip')}
                                </div>
                            </>
                        ),
                        text4Ok: null,
                        text4Cancel: null,
                        width: 655,
                        height: 305,
                        onCancel: () => {
                            Dialog.hide(submitDialog);
                        }
                    });
                })
                .catch((error) => {
                    const err = get(error, 'response.data', {});
                    Message.add({
                        messageType: 'toast',
                        message: getErrorMsg(err) || Intl.get('submit.fail'),
                        type: 'error'
                    });
                });
        }
    };

    const validateForm = () => {
        Promise.all([receiptRef.current.core.validate(), deliveryRef.current.core.validate()])
            .then(() => {
                setFormStage(false);
            })
            .catch(() => {});
    };

    const getValue = (name: string) => {
        if (name === 'tax_cert') {
            return get(dataValue, name, [{}]);
        } else {
            return get(dataValue, name, get(defaultValue, name, ''));
        }
    };

    const getSubmitValue = () => {
        const submitValue = { content_type: RECEIPT_CONTENT_TYPE.tech_service.value };
        const submitFields = get(dataFields, receiptType);
        forEach(submitFields, (fieldName: string) => {
            set(submitValue, fieldName, getValue(fieldName));
        });
        return submitValue;
    };

    return (
        <Dialog
            className="fx-receipt-dialog"
            width={655}
            height={625}
            title={Intl.get('corp.order.operation.invoice')}
            visible={showReceiptDialog}
            text4Cancel={formStage ? Intl.get('cancel') : Intl.get('returnAndEdit')}
            text4Ok={formStage ? Intl.get('ok') : Intl.get('confirmInfo')}
            onOk={handleOk}
            onCancel={(_e, effect) => {
                // 点击右上角关闭按钮
                if (effect === 'close') {
                    onCancel();
                }
                // 点击右下角cancel按钮
                if (effect === 'cancel') {
                    if (!formStage) {
                        setFormStage(true);
                    } else {
                        onCancel();
                    }
                }
            }}
        >
            {!formStage && <div className="warn-info">{Intl.get('receipt.submit.tip')}</div>}
            {renderReceipInfo()}
        </Dialog>
    );
};

export default ReceiptDialog;
